import { Volume as VolumeSchema } from '@schema';
import {
  VOLUME_INTERNAL_BACKEND,
  VOLUME_STATUS_MAP,
  VOLUME_STATUSES,
  VOLUME_STATUSES_GROUPS
} from '@/common/constants/volumes.constants';
import { store } from '@/common/root';

const mapper = (volume: VolumeSchema) => {
  const { status } = volume;

  const serverId = volume.attachments?.[0]?.server_id;
  const server = store.getters['servers/getItemById'](serverId);

  const isInProgress = VOLUME_STATUSES_GROUPS.InProgress.includes(status);
  const isLoading = isInProgress;
  const isInUse = VOLUME_STATUSES_GROUPS.InUse.includes(status);
  const isDetachableInCommon = isInUse && server && server.bootableVolumeId !== volume.id && !server.isShelved;
  const isErrored = VOLUME_STATUSES_GROUPS.Error.includes(status);
  const isReserved = status === VOLUME_STATUSES.RESERVED;
  const canCreateBackup = VOLUME_STATUSES_GROUPS.CanCreateBackup.includes(status);

  const canCreateSnapshot = (() => {
    if (isInProgress || isErrored || isReserved) {
      return false;
    }

    return !(server && (server.isInProgress || server.isShelved || server.isSuspended));
  })();

  return {
    id: volume.id,
    name: volume.name,
    status,
    storage_policy_name: volume.storage_policy_name,
    statusIcon: VOLUME_STATUS_MAP[status].icon,
    statusText: VOLUME_STATUS_MAP[status].text,
    createdAt: volume.created_at,
    updatedAt: volume.updated_at,
    server,
    serverId,
    isInUse,
    isErrored,
    isAvailable: status === VOLUME_STATUSES.AVAILABLE,
    isInProgress,
    isReserved,
    isLoading: isInProgress,
    isDeletable: !isLoading && !isInUse,
    isDetachableInCommon: isInUse && server && server.bootableVolumeId !== volume.id && !server.isShelved,
    isDetachable: isDetachableInCommon && server.isStopped,
    isForceDetachable: isDetachableInCommon && !server.isStopped,
    isClonable: (!isInUse || (server && server.isShutoff)) && volume.volume_image_metadata?.disk_format !== 'iso',
    isResetable: isInProgress || isErrored || status === VOLUME_STATUSES.RESERVED,
    canCreateSnapshot,
    policy: volume.storage_policy_name,
    snapshotId: volume.snapshot_id,
    attachedTo: volume.attachments?.[0]?.server_name,
    domainId: volume.domain_id,
    serverIds: volume.attachments?.map(ser => ser.server_id),
    size: volume.size,
    isBootable: volume.bootable,
    serial: volume.id,
    projectId: volume.project_id,
    meta: volume.volume_image_metadata,
    traits: volume.traits,
    format: volume.volume_image_metadata?.disk_format,
    hypervisorType: volume.volume_image_metadata?.hypervisor_type || 'QEMU',
    uefi: volume.volume_image_metadata?.hw_firmware_type === 'uefi',
    vtpm: volume.volume_image_metadata?.hw_tpm_version === '2.0',
    secureBoot: volume.volume_image_metadata?.os_secure_boot === 'required',
    backupPlans: volume.backup_plans,
    volumeBackend: volume.volume_backend,
    isInternal: volume.volume_backend === VOLUME_INTERNAL_BACKEND,
    canCreateBackup
  };
};

export type Volume = ReturnType<typeof mapper>;

export default mapper;
